import { Baby, ArrowLeft, Check, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';

interface PregnancyTypeStepProps {
  value: number;
  onChange: (count: number) => void;
  onNext: () => void;
  onBack: () => void;
}

export function PregnancyTypeStep({ value, onChange, onNext, onBack }: PregnancyTypeStepProps) {
  const { t } = useTranslation('onboarding');

  const options = [
    { value: 1, label: t('pregnancyType.singleton'), description: t('pregnancyType.singletonDesc') },
    { value: 2, label: t('pregnancyType.twins'), description: t('pregnancyType.twinsDesc') },
    { value: 3, label: t('pregnancyType.triplets'), description: t('pregnancyType.tripletsDesc') },
    { value: 4, label: t('pregnancyType.quadruplets'), description: t('pregnancyType.quadrupletsDesc') },
  ];

  const isMultiples = value > 1;

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        <Baby className="w-8 h-8 text-pp-orange" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {t('pregnancyType.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {t('pregnancyType.description')}
      </p>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all',
              value === option.value
                ? 'border-pp-orange bg-pp-orange/20'
                : 'border-white/20 bg-white/5 hover:border-white/30'
            )}
          >
            <div className="text-left">
              <span
                className={cn(
                  'font-medium block',
                  value === option.value ? 'text-white' : 'text-white/90'
                )}
              >
                {option.label}
              </span>
              <span className="text-sm text-white/50">{option.description}</span>
            </div>
            {value === option.value && (
              <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Info for multiples */}
      {isMultiples && (
        <div className="bg-amber-500/20 rounded-[10px] p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-white">{t('pregnancyType.multiplesTitle')}</p>
              <p className="text-sm text-white/70 mt-1">
                {t('pregnancyType.multiplesInfo')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onNext}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors"
      >
        {t('common.next')}
      </button>
    </div>
  );
}
